import { SaaSPackage, GroupedPackage } from '@/types';

/**
 * Transforms an array of flat CRM packages into a grouped array suitable for UI display.
 * Maps the cost of specific billing intervals (Month, Quarter, Year) to their respective
 * monthly_price, quarterly_price, and annual_price fields, while keeping track of their
 * exact package_code.
 */
export function groupPackages(packages: SaaSPackage[]): GroupedPackage[] {
  const groups: Record<string, GroupedPackage> = {};

  packages.forEach(pkg => {
    if (!groups[pkg.package_name]) {
       groups[pkg.package_name] = {
         ...pkg,
         monthly_price: pkg.monthly_price || 0,
         quarterly_price: pkg.quarterly_price || 0,
         annual_price: pkg.annual_price || 0,
       };
    }
    
    const target = groups[pkg.package_name];
    
    if (pkg.billing_interval === 'Month') {
       target.monthly_price = pkg.cost || 0;
       target.monthly_package_code = pkg.package_code;
       target.feature_list = target.feature_list || pkg.feature_list;
       target.add_ons = target.add_ons || pkg.add_ons;
    } else if (pkg.billing_interval === 'Quarter') {
       target.quarterly_price = pkg.cost || 0;
       target.quarterly_package_code = pkg.package_code;
       target.feature_list = target.feature_list || pkg.feature_list;
       target.add_ons = target.add_ons || pkg.add_ons;
    } else if (pkg.billing_interval === 'Year') {
       target.annual_price = pkg.cost || 0;
       target.annual_package_code = pkg.package_code;
       target.feature_list = target.feature_list || pkg.feature_list;
       target.add_ons = target.add_ons || pkg.add_ons;
    } else if (!pkg.billing_interval) {
       if (pkg.is_custom_price) {
          target.is_custom_price = true;
          target.monthly_package_code = target.monthly_package_code || pkg.package_code;
          target.annual_package_code = target.annual_package_code || pkg.package_code;
       }
       // Only assign to monthly if it's explicitly a monthly legacy package, 
       // but based on request "only show in monthly if its interval matches", 
       // we should not assume it's monthly if interval is missing, unless we have to.
       // The user requested to only show in monthly if interval matches.
    }
  });

  return Object.values(groups).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
}

export function getCyclePackageCode(pkg: GroupedPackage, cycle: string): string {
  if (cycle === 'monthly' && pkg.monthly_package_code) return pkg.monthly_package_code;
  if (cycle === 'quarterly' && pkg.quarterly_package_code) return pkg.quarterly_package_code;
  if (cycle === 'annual' && pkg.annual_package_code) return pkg.annual_package_code;
  return pkg.package_code;
}
